import type {ComponentProps} from "react";
import {forwardRef} from "react";
import cx from "../../utils/cx";
import spriteUrl from "./assets/_generated/sprite.svg?url";
import {iconStyles} from "./DSIcon.css";

type IconPart = ["stroke" | "secondaryFill" | "strokeWithFill" | "fill" | "customFill", id: string];

const icons = {
  AddCard: [["stroke", "add_card_24_stroke"]],
  Archive: [["strokeWithFill", "archive_24_filled_stroke"]],
  ArrowCurvedLeft: [["stroke", "arrow_curved_left_24_stroke"]],
  ArrowCurvedRight: [["stroke", "arrow_curved_right_24_stroke"]],
  ArrowCurvedUp: [["stroke", "arrow_curved_up_24_stroke"]],
  ArrowRight: [["stroke", "arrow_right_24_stroke"]],
  Attachment16: [["stroke", "attachment_16_stroke"]],
  Attachment24: [["stroke", "attachment_24_stroke"]],
  Average: [["stroke", "average_24_stroke"]],
  BarChart: [["stroke", "barchart_24_stroke"]],
  Ban: [["stroke", "ban_24_stroke"]],
  Beast: [["fill", "beast_24_fill"]],
  BeastSlain: [["fill", "beast_slain_24_fill"]],
  Bell: [["strokeWithFill", "bell_24_filled_stroke"]],
  Bitbucket: [["customFill", "bitbucket_24_custom_fill"]],
  Blocked: [
    ["fill", "blocked_24_fill"],
    ["stroke", "blocked_24_stroke"],
  ],
  Bold: [["fill", "bold_24_fill"]],
  Book: [["stroke", "book_24_stroke"]],
  Bookmark: [["strokeWithFill", "bookmark_24_filled_stroke"]],
  Brackets: [["stroke", "brackets_24_stroke"]],
  Bug: [["strokeWithFill", "bug_24_filled_stroke"]],
  Calendar: [["stroke", "calendar_24_stroke"]],
  CalendarBell: [
    ["strokeWithFill", "calendar_bell_24_filled_stroke"],
    ["stroke", "calendar_bell_24_stroke"],
  ],
  CalendarMilestone: [
    ["strokeWithFill", "calendar_milestone_24_filled_stroke"],
    ["stroke", "calendar_milestone_24_stroke"],
  ],
  Card: [["stroke", "card_24_stroke"]],
  CardTriplet: [["strokeWithFill", "card_triplet_24_filled_stroke"]],
  Check: [["stroke", "check_24_stroke"]],
  CheckBoxed: [["stroke", "check_boxed_24_stroke"]],
  ChevronDown: [["stroke", "chevron_down_24_stroke"]],
  ChevronLeft: [["stroke", "chevron_left_24_stroke"]],
  ChevronRight: [["stroke", "chevron_right_24_stroke"]],
  ChevronUp: [["stroke", "chevron_up_24_stroke"]],
  Clock: [["strokeWithFill", "clock_24_filled_stroke"]],
  Close: [["stroke", "close_24_stroke"]],
  Code: [["stroke", "code_24_stroke"]],
  Cog: [["stroke", "cog_24_stroke"]],
  Collapse: [["stroke", "collapse_24_stroke"]],
  Conversation: [["strokeWithFill", "conversation_24_filled_stroke"]],
  Crown: [["stroke", "crown_24_stroke"]],
  Deck: [["stroke", "deck_24_stroke"]],
  DeckInbox: [["stroke", "deck_inbox_24_stroke"]],
  Discord: [["fill", "discord_24_fill"]],
  Doc: [["strokeWithFill", "doc_24_filled_stroke"]],
  DotsVer: [["fill", "dots_ver_24_fill"]],
  DotsHor: [["fill", "dots_hor_24_fill"]],
  Download: [["stroke", "download_24_stroke"]],
  Drag: [["stroke", "drag_24_stroke"]],
  DuplicateCard: [["stroke", "duplicate_card_24_stroke"]],
  Effort: [["stroke", "effort_24_stroke"]],
  Expand: [["stroke", "expand_24_stroke"]],
  External: [["stroke", "external_24_stroke"]],
  Eye: [["stroke", "eye_24_stroke"]],
  EyeSlash: [["stroke", "eye_slash_24_stroke"]],
  Facebook: [["fill", "facebook_24_fill"]],
  Filter: [["stroke", "filter_24_stroke"]],
  Flash: [["strokeWithFill", "flash_24_filled_stroke"]],
  FlashSlash: [["stroke", "flash_slash_24_stroke"]],
  FocusMode: [["stroke", "focus_mode_24_stroke"]],
  Fullscreen: [["stroke", "fullscreen_24_stroke"]],
  GateClosed20: [
    ["secondaryFill", "gate_closed_20_secondary_fill"],
    ["stroke", "gate_closed_20_stroke"],
  ],
  GateClosed24: [["stroke", "gate_closed_24_stroke"]],
  GateOpen20: [["stroke", "gate_open_20_stroke"]],
  GateOpen24: [["stroke", "gate_open_24_stroke"]],
  Gdd: [["stroke", "gdd_24_stroke"]],
  Ghost: [
    ["fill", "ghost_24_fill"],
    ["stroke", "ghost_24_stroke"],
  ],
  Gift: [["stroke", "gift_24_stroke"]],
  Github: [["fill", "github_24_fill"]],
  Gitlab: [["customFill", "gitlab_24_custom_fill"]],
  Globe: [["stroke", "globe_24_stroke"]],
  Google: [["customFill", "google_24_custom_fill"]],
  Hand: [["stroke", "hand_24_stroke"]],
  HandSingle: [["stroke", "hand_single_24_stroke"]],
  HandSingleDashed: [["stroke", "hand_single_dashed_24_stroke"]],
  Headings: [["stroke", "headings_24_stroke"]],
  History: [["stroke", "history_24_stroke"]],
  Info: [
    ["strokeWithFill", "info_24_filled_stroke"],
    ["fill", "info_24_fill"],
  ],
  Instagram: [["stroke", "instagram_24_stroke"]],
  Italic: [["stroke", "italic_24_stroke"]],
  Journey: [["stroke", "journey_24_stroke"]],
  Joystick: [["stroke", "joystick_24_stroke"]],
  Keyboard: [
    ["fill", "keyboard_24_fill"],
    ["stroke", "keyboard_24_stroke"],
  ],
  Link: [["stroke", "link_24_stroke"]],
  LinkedIn: [["fill", "linkedin_24_fill"]],
  List: [["stroke", "list_24_stroke"]],
  LockClosed: [["stroke", "lock_closed_24_stroke"]],
  // LockOpen: [["stroke", "lock_open_24_stroke"]],
  Mail: [["stroke", "mail_24_stroke"]],
  Media: [["stroke", "media_24_stroke"]],
  Mention: [["stroke", "mention_24_stroke"]],
  Milestone: [["strokeWithFill", "milestone_24_filled_stroke"]],
  MilestoneNext: [["strokeWithFill", "milestone_next_24_filled_stroke"]],
  Minus: [["stroke", "minus_24_stroke"]],
  Misc: [["stroke", "misc_24_stroke"]],
  Palette: [["stroke", "palette_24_stroke"]],
  Pencil: [["stroke", "pencil_24_stroke"]],
  Pin: [["stroke", "pin_24_stroke"]],
  Play: [["strokeWithFill", "play_24_filled_stroke"]],
  Plus: [["stroke", "plus_24_stroke"]],
  PrioA: [["stroke", "prio_a_24_stroke"]],
  PrioB: [["stroke", "prio_b_24_stroke"]],
  PrioC: [["stroke", "prio_c_24_stroke"]],
  Properties: [["stroke", "properties_24_stroke"]],
  Pyramid: [["stroke", "pyramid_24_stroke"]],
  // QA: [["stroke", "qa_24_stroke"], ["fill", "qa_24_fill"]],
  Question: [
    ["strokeWithFill", "question_24_filled_stroke"],
    ["fill", "question_24_fill"],
  ],
  Reference: [["stroke", "reference_24_stroke"]],
  Reminder: [["stroke", "reminder_24_stroke"]],
  Revenue: [
    ["fill", "revenue_24_fill"],
    ["stroke", "revenue_24_stroke"],
  ],
  Review: [
    ["fill", "review_24_fill"],
    ["stroke", "review_24_stroke"],
  ],
  Robot: [["stroke", "robot_24_stroke"]],
  Rogue: [
    ["secondaryFill", "rogue_24_secondary_fill"],
    ["stroke", "rogue_24_stroke"],
  ],
  Search: [["stroke", "search_24_stroke"]],
  SearchAdd16: [["stroke", "search_add_16_stroke"]],
  SelectionClose: [
    ["strokeWithFill", "selection_empty_24_filled_stroke"],
    ["stroke", "selection_close_24_stroke"],
  ],
  SelectionEmpty: [["strokeWithFill", "selection_empty_24_filled_stroke"]],
  Share: [["stroke", "share_24_stroke"]],
  Shield: [
    ["strokeWithFill", "shield_24_filled_stroke"],
    ["fill", "shield_24_fill"],
  ],
  Signout: [["stroke", "signout_24_stroke"]],
  Smile: [
    ["fill", "smile_24_fill"],
    ["stroke", "smile_24_stroke"],
  ],
  Snoozing: [
    ["fill", "snoozing_24_fill"],
    ["stroke", "snoozing_24_stroke"],
  ],
  Sort: [["fill", "sort_24_fill"]],
  SortAsc: [["stroke", "sort_asc_24_stroke"]],
  SortDesc: [["stroke", "sort_desc_24_stroke"]],
  Sprint: [
    ["secondaryFill", "sprint_24_secondary_fill"],
    ["stroke", "sprint_24_stroke"],
  ],
  SprintNext: [
    ["secondaryFill", "sprint_next_24_secondary_fill"],
    ["stroke", "sprint_next_24_stroke"],
  ],
  Star: [["strokeWithFill", "star_24_filled_stroke"]],
  Stop: [["stroke", "stop_24_stroke"]],
  StopWatch: [["stroke", "stop_watch_24_stroke"]],
  StrikeThrough: [["stroke", "strikethrough_24_stroke"]],
  Tag: [["stroke", "tag_24_stroke"]],
  Tasks: [["stroke", "tasks_24_stroke"]],
  Telescope: [["stroke", "telescope_24_stroke"]],
  Text: [["fill", "text_24_fill"]],
  Trash: [["stroke", "trash_24_stroke"]],
  Unplay: [["stroke", "un_play_24_stroke"]],
  User: [["stroke", "user_24_stroke"]],
  UserAdd: [["stroke", "user_add_24_stroke"]],
  Warning: [["strokeWithFill", "warning_24_filled_stroke"]],
  WhatsApp: [["fill", "whatsapp_24_fill"]],
  X: [["fill", "x_24_fill"]],
  Youtube: [["fill", "youtube_24_fill"]],
} satisfies Record<string, IconPart[]>;

export const allIconNames = Object.keys(icons) as IconName[];

export type IconName = keyof typeof icons;

export type DSIconProps = {
  name: IconName;
  label: null | string;
  size?: keyof typeof iconStyles.sizes | null;
  setSize?: keyof typeof iconStyles.setSize | null;
  inline?: boolean;
} & Pick<ComponentProps<"svg">, "onClick" | "style" | "className">;

export const DSIcon = forwardRef<SVGSVGElement, DSIconProps>((props, ref) => {
  const {name, size, setSize, label, className, inline, ...rest} = props;
  const parts = icons[name];
  if (process.env.NODE_ENV === "development") {
    if (!parts) throw new Error(`Invalid icon name: ${name}, props: ${JSON.stringify(props)}`);
  }

  return (
    <svg
      role="img"
      width="24"
      height="24"
      className={cx(
        iconStyles.base,
        inline ? iconStyles.mode.inline : iconStyles.mode.block,
        size && iconStyles.sizes[size],
        setSize && iconStyles.setSize[setSize],
        className
      )}
      aria-hidden={label ? undefined : "true"}
      aria-label={label || undefined}
      {...rest}
      ref={ref}
    >
      {(parts ?? []).map(([type, id]) => (
        <use
          href={`${spriteUrl}#${id}`}
          className={type !== "customFill" ? iconStyles.type[type] : undefined}
          key={type}
        />
      ))}
    </svg>
  );
});
